import { useState } from "react";
import { SbNotification } from "uiCore";
import useWorkforcePlanStore from "Store/useWorkforcePlanStore";
import { addTalentMobilityWorkforcePlan, updateTalentMobilityWorkforcePlan } from "Api/apiService";

// Shared Save Plan / Save & Close Plan flow for Workforce Planning — mirrors
// useRedeploymentPlanActions exactly (see that file's comments for the
// create-vs-update/closeAfter reasoning), except the page builds the final
// payload rather than the store: WorkforcePlanningV2's demand list includes
// roles whose target came pre-set on the fetched data (never session-
// edited), which the store's own getWorkforcePlanPayload doesn't know
// about, so `getPayload(trimmedPlanName)` is injected rather than called
// here directly. `hasDraft` is likewise passed in rather than computed
// here, for the same reason — it also needs to know each demand role's
// Current Positions, which only the page has.
const useWorkforcePlanActions = ({ hasDraft, getPayload, onClosed } = {}) => {
  const planId = useWorkforcePlanStore((state) => state.planId);
  const planName = useWorkforcePlanStore((state) => state.planName);
  const setPlan = useWorkforcePlanStore((state) => state.setPlan);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const isUpdate = Boolean(planId);
  const planLabel = isUpdate ? planName : "New Plan";

  // Returns true on success so the calling button knows it can close its
  // modal (left open on failure, so the typed name isn't lost on a retry).
  const savePlan = async (rawPlanName, { closeAfter = false } = {}) => {
    if (!hasDraft) {
      SbNotification.info("Nothing to save", "No workforce plan data has been drafted yet.");
      return false;
    }
    const trimmedPlanName = (rawPlanName || "").trim();
    if (!trimmedPlanName) {
      SbNotification.info("Plan name required", "Please enter a plan name.");
      return false;
    }

    const payload = getPayload(trimmedPlanName);
    setIsSubmitting(true);
    try {
      const response = isUpdate
        ? await updateTalentMobilityWorkforcePlan(planId, payload)
        : await addTalentMobilityWorkforcePlan(payload);
      const savedPlanName = response?.plan_name || trimmedPlanName;

      if (closeAfter) {
        // The draft is now the backend's — clear it (and the flow's session
        // shortlist tags) so the next draft starts from scratch. reset()
        // also clears planId/planName, so the header goes back to "New Plan".
        useWorkforcePlanStore.getState().reset();
      } else {
        setPlan(response?.plan_id ?? planId, savedPlanName);
      }

      SbNotification.success(
        isUpdate ? "Workforce plan updated" : "Workforce plan saved",
        `"${savedPlanName}" plan ${isUpdate ? "updated" : "saved"}.`
      );
      if (closeAfter) onClosed?.();
      return true;
    } catch (error) {
      SbNotification.error(
        isUpdate ? "Unable to update workforce plan" : "Unable to save workforce plan",
        error?.response?.data?.message || "Please try again."
      );
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return { isSubmitting, planId, planName, planLabel, isUpdate, savePlan };
};

export default useWorkforcePlanActions;
